import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import axiosApiInstance from '../../api/axiosClient';
import {
    InstagramContent,
    InstagramPost,
    InstagramStory,
    InstagramReel,
    TrackedCreator,
    Topic,
    ScraperState,
    ContentFilters,
    PaginationParams,
    ApiResponse,
    CreateCreatorRequest,
    CreateTopicRequest,
    EnrichContentRequest,
} from './types';

const API_BASE = '/api/instagram-influencers';

// Content Queries
export const useContent = (filters: ContentFilters & PaginationParams) => {
    return useQuery({
        queryKey: ['instagram-content', filters],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<InstagramContent[]>>(`${API_BASE}/content`, {
                params: filters
            });
            return response.data;
        },
        keepPreviousData: true,
    });
};

// Type-specific content queries
export const usePosts = (filters: ContentFilters & PaginationParams) => {
    return useQuery({
        queryKey: ['instagram-posts', filters],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<InstagramPost[]>>(`${API_BASE}/content/posts`, {
                params: filters
            });
            // console.log('usePosts response:', response.data);
            return response.data;
        },
        keepPreviousData: true,
    });
};

export const useStories = (filters: ContentFilters & PaginationParams) => {
    return useQuery({
        queryKey: ['instagram-stories', filters],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<InstagramStory[]>>(`${API_BASE}/content/stories`, {
                params: filters
            });
            return response.data;
        },
        keepPreviousData: true,
    });
};

export const useReels = (filters: ContentFilters & PaginationParams) => {
    return useQuery({
        queryKey: ['instagram-reels', filters],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<InstagramReel[]>>(`${API_BASE}/content/reels`, {
                params: filters
            });
            return response.data;
        },
        keepPreviousData: true,
    });
};

export const useEnrichContent = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ payload, contentType }: { payload: any, contentType: string }) => {
            // Determine the correct endpoint based on content type
            let endpoint = `${API_BASE}/content/enrich`; // fallback
            if (contentType === 'post') {
                endpoint = `${API_BASE}/content/posts/enrich`;
            } else if (contentType === 'story') {
                endpoint = `${API_BASE}/content/stories/enrich`;
            } else if (contentType === 'reel') {
                endpoint = `${API_BASE}/content/reels/enrich`;
            }

            const response = await axiosApiInstance.post<ApiResponse<InstagramContent>>(
                endpoint,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-content'] });
            queryClient.invalidateQueries({ queryKey: ['instagram-posts'] });
            queryClient.invalidateQueries({ queryKey: ['instagram-stories'] });
            queryClient.invalidateQueries({ queryKey: ['instagram-reels'] });
            message.success('Content enriched successfully');
        },
        onError: (error: any) => {
            // Error handling is already done by axios interceptor, but we can add specific messaging
            console.error('Content enrichment failed:', error);
        },
    });
};

export const useUploadScreenshots = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (formData: FormData) => {
            const response = await axiosApiInstance.post<ApiResponse<any>>(
                `${API_BASE}/content/upload-screenshots`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-content'] });
            message.success('Screenshots uploaded and analyzed successfully');
        },
        onError: (error: any) => {
            console.error('Screenshot upload failed:', error);
        },
    });
};

// Tracked Creators Queries
export const useTrackedCreators = () => {
    return useQuery({
        queryKey: ['instagram-tracked-creators'],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<TrackedCreator[]>>(`${API_BASE}/tracked-creators`);
            return response.data;
        },
    });
};

export const useCreateCreator = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateCreatorRequest) => {
            const response = await axiosApiInstance.post<ApiResponse<TrackedCreator>>(`${API_BASE}/tracked-creators`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-tracked-creators'] });
            message.success('Creator added successfully');
        },
        onError: (error: any) => {
            console.error('Creator creation failed:', error);
        },
    });
};

export const useDeleteCreator = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await axiosApiInstance.delete<ApiResponse<void>>(`${API_BASE}/tracked-creators/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-tracked-creators'] });
            message.success('Creator deleted successfully');
        },
        onError: (error: any) => {
            console.error('Creator deletion failed:', error);
        },
    });
};

export const useRefreshCreatorUserID = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await axiosApiInstance.post<ApiResponse<TrackedCreator>>(`${API_BASE}/tracked-creators/${id}/refresh-userid`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-tracked-creators'] });
            message.success('Instagram User ID refreshed successfully');
        },
        onError: (error: any) => {
            console.error('User ID refresh failed::', error);
        },
    });
};

// Topics Queries
export const useTopics = () => {
    return useQuery({
        queryKey: ['instagram-topics'],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<Topic[]>>(`${API_BASE}/topics`);
            return response.data;
        },
    });
};

export const useCreateTopic = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateTopicRequest) => {
            const response = await axiosApiInstance.post<ApiResponse<Topic>>(`${API_BASE}/topics`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-topics'] });
            message.success('Topic created successfully');
        },
        onError: (error: any) => {
            console.error('Topic creation failed:', error);
        },
    });
};

export const useDeleteTopic = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await axiosApiInstance.delete<ApiResponse<void>>(`${API_BASE}/topics/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-topics'] });
            message.success('Topic deleted successfully');
        },
        onError: (error: any) => {
            console.error('Topic deletion failed:', error);
        },
    });
};

// Scraper Operations
export const useTriggerScrape = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await axiosApiInstance.post<ApiResponse<void>>(`${API_BASE}/scrape`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-content'] });
            queryClient.invalidateQueries({ queryKey: ['instagram-tracked-creators'] });
            message.success('Scraping started successfully');
        },
        onError: (error: any) => {
            console.error('Scraping trigger failed:', error);
        },
    });
};

export const useTriggerSpecificScrape = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (contentType: string) => {
            const endpoint = contentType === 'all'
                ? `${API_BASE}/scrape/all`
                : `${API_BASE}/scrape/${contentType}`;

            const response = await axiosApiInstance.post<ApiResponse<void>>(endpoint);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-content'] });
            queryClient.invalidateQueries({ queryKey: ['instagram-tracked-creators'] });
            message.success('Scraping started successfully');
        },
        onError: (error: any) => {
            console.error('Specific scraping trigger failed:', error);
        },
    });
};

export const useTriggerProfileScrape = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await axiosApiInstance.post<ApiResponse<void>>(`${API_BASE}/scrape/tracked-creators-profiles`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-tracked-creators'] });
            message.success('Profile updates started successfully');
        },
        onError: (error: any) => {
            console.error('Profile scraping trigger failed:', error);
        },
    });
};

// Scraping Status
export const useScrapingStatus = () => {
    return useQuery({
        queryKey: ['instagram-scraping-status'],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<any>>(`${API_BASE}/scraping-status`);
            return response.data;
        },
        refetchInterval: (data) => data?.data?.isRunning ? 2000 : 30000, // Faster refresh when running
    });
};

// Filter Settings
export const useFilterSettings = () => {
    return useQuery({
        queryKey: ['instagram-filter-settings'],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<any>>(`${API_BASE}/filter-settings`);
            return response.data;
        },
    });
};

export const useUpdateFilterSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: { hashtags: string; mentions: string; enabled: boolean }) => {
            const response = await axiosApiInstance.put<ApiResponse<any>>(`${API_BASE}/filter-settings`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-filter-settings'] });
            queryClient.invalidateQueries({ queryKey: ['instagram-filter-stats'] });
            message.success('Filter settings updated successfully');
        },
        onError: (error: any) => {
            console.error('Filter settings update failed:', error);
        },
    });
};

export const useFilterStats = () => {
    return useQuery({
        queryKey: ['instagram-filter-stats'],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<any>>(`${API_BASE}/filter-stats`);
            return response.data;
        },
    });
};

// Cache Management
export const useCacheStatistics = () => {
    return useQuery({
        queryKey: ['instagram-cache-statistics'],
        queryFn: async () => {
            const response = await axiosApiInstance.get<ApiResponse<any>>(`${API_BASE}/cache/statistics`);
            return response.data;
        },
        refetchInterval: 30000, // Refresh every 30 seconds
    });
};

export const useCleanupCache = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await axiosApiInstance.post<ApiResponse<void>>(`${API_BASE}/cache/cleanup`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-cache-statistics'] });
            message.success('Expired cache entries cleaned up successfully');
        },
        onError: (error: any) => {
            console.error('Cache cleanup failed:', error);
        },
    });
};

export const useClearCache = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await axiosApiInstance.post<ApiResponse<void>>(`${API_BASE}/cache/clear`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instagram-cache-statistics'] });
            message.success('All cache cleared successfully');
        },
        onError: (error: any) => {
            console.error('Cache clear failed:', error);
        },
    });
};
